import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { solicitarCodigo, verificarCodigo, redefinirSenha } from "../../api/redefinir";

export default function Redefinir() {
  const navigate = useNavigate();
  
  // Estados
  const [etapa, setEtapa] = useState(1); // 1: email, 2: código, 3: nova senha
  const [emailOuUsuario, setEmailOuUsuario] = useState("");
  const [codigo, setCodigo] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Etapa 1: Solicitar código
  const handleSolicitarCodigo = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!emailOuUsuario.trim()) {
      setErrorMessage("Por favor, insira seu email ou usuário.");
      return;
    }

    setIsLoading(true);
    try {
      await solicitarCodigo(emailOuUsuario);
      setSuccessMessage("Código enviado! Verifique seu email.");
      setTimeout(() => {
        setEtapa(2);
        setSuccessMessage("");
      }, 2000);
    } catch (err) {
      setErrorMessage(err.message || "Usuário não encontrado.");
    } finally {
      setIsLoading(false);
    }
  };

  // Etapa 2: Verificar código
  const handleVerificarCodigo = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!codigo.trim()) {
      setErrorMessage("Por favor, insira o código.");
      return;
    }

    setIsLoading(true);
    try {
      await verificarCodigo(emailOuUsuario, codigo);
      setSuccessMessage("Código verificado! Defina sua nova senha.");
      setTimeout(() => {
        setEtapa(3);
        setSuccessMessage("");
      }, 1500);
    } catch (err) {
      setErrorMessage(err.message || "Código inválido ou expirado.");
    } finally {
      setIsLoading(false);
    }
  };

  // Etapa 3: Redefinir senha
  const handleRedefinirSenha = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    // Validação de comprimento mínimo
    if (novaSenha.length < 8) {
      setErrorMessage("A senha deve ter pelo menos 8 caracteres.");
      return;
    }

    // Validação de letra
    if (!/[a-zA-Z]/.test(novaSenha)) {
      setErrorMessage("A senha deve conter pelo menos uma letra.");
      return;
    }

    // Validação de número
    if (!/[0-9]/.test(novaSenha)) {
      setErrorMessage("A senha deve conter pelo menos um número.");
      return;
    }

    // Validação de confirmação
    if (novaSenha !== confirmarSenha) {
      setErrorMessage("As senhas não coincidem.");
      return;
    }

    setIsLoading(true);
    try {
      await redefinirSenha(emailOuUsuario, codigo, novaSenha);
      setSuccessMessage("Senha redefinida com sucesso!");
      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (err) {
      setErrorMessage(err.message || "Erro ao redefinir senha.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-green-100 to-emerald-100 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h2 className="text-[5vh] font-bold text-gray-800 tracking-tight">
            Redefinir Senha
          </h2>
          <p className="mt-2 text-[2.5vh] text-gray-600">
            {etapa === 1 && "Informe seu email ou usuário"}
            {etapa === 2 && "Digite o código recebido"}
            {etapa === 3 && "Crie sua nova senha"}
          </p>
        </div>

        {/* Indicador de etapas */}
        <div className="flex justify-center gap-2">
          <div className={`h-2 w-16 rounded-full ${etapa >= 1 ? 'bg-green-500' : 'bg-gray-300'}`} />
          <div className={`h-2 w-16 rounded-full ${etapa >= 2 ? 'bg-green-500' : 'bg-gray-300'}`} />
          <div className={`h-2 w-16 rounded-full ${etapa >= 3 ? 'bg-green-500' : 'bg-gray-300'}`} />
        </div>

        <div className="p-0">
          {errorMessage && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-red-700 text-sm font-medium">{errorMessage}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200">
              <p className="text-green-700 text-sm font-medium">{successMessage}</p>
            </div>
          )}

          {/* ETAPA 1: Email/Usuário */}
          {etapa === 1 && (
            <form onSubmit={handleSolicitarCodigo} className="space-y-6">
              <div>
                <p className="text-gray-700 font-semibold text-[2vh] mb-2">
                  Email ou Usuário
                </p>
                <input
                  type="text"
                  required
                  value={emailOuUsuario}
                  onChange={(e) => setEmailOuUsuario(e.target.value.toLowerCase())}
                  className="w-full px-5 py-3.5 border border-green-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 bg-white/70 backdrop-blur-sm placeholder-gray-500 text-gray-800"
                  placeholder="Digite seu email ou usuário"
                />
              </div>

              <div className="flex flex-col gap-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white py-3 px-4 rounded-xl font-semibold shadow-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 disabled:opacity-70"
                >
                  {isLoading ? "Enviando..." : "Enviar Código"}
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="w-full bg-white border border-green-300 text-gray-700 py-3 px-4 rounded-xl font-semibold shadow-md hover:bg-gray-50 transition-all duration-200"
                >
                  <span className="flex items-center justify-center">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 19l-7-7m0 0l7-7m-7 7h18"
                      />
                    </svg>
                    Voltar
                  </span>
                </button>
              </div>
            </form>
          )}

          {/* ETAPA 2: Código de Verificação */}
          {etapa === 2 && (
            <form onSubmit={handleVerificarCodigo} className="space-y-6">
              <div>
                <p className="text-gray-700 font-semibold text-[2vh] mb-2">
                  Código de Verificação
                </p>
                <input
                  type="text"
                  required
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  className="w-full px-5 py-3.5 border border-green-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 bg-white/70 backdrop-blur-sm placeholder-gray-500 text-gray-800 text-center text-2xl tracking-widest"
                  placeholder="XXXXXX"
                  maxLength="6"
                />
                <p className="mt-2 text-sm text-gray-600 text-center">
                  Código enviado para {emailOuUsuario}
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white py-3 px-4 rounded-xl font-semibold shadow-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 disabled:opacity-70"
                >
                  {isLoading ? "Verificando..." : "Verificar Código"}
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="w-full bg-white border border-green-300 text-gray-700 py-3 px-4 rounded-xl font-semibold shadow-md hover:bg-gray-50 transition-all duration-200"
                >
                  <span className="flex items-center justify-center">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 19l-7-7m0 0l7-7m-7 7h18"
                      />
                    </svg>
                    Voltar
                  </span>
                </button>
              </div>
            </form>
          )}

          {/* ETAPA 3: Nova Senha */}
          {etapa === 3 && (
            <form onSubmit={handleRedefinirSenha} className="space-y-6">
              <div>
                <p className="text-gray-700 font-semibold text-[2vh] mb-2">
                  Nova Senha
                </p>
                <input
                  type="password"
                  required
                  value={novaSenha}
                  onChange={(e) => setNovaSenha(e.target.value)}
                  className="w-full px-5 py-3.5 border border-green-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 bg-white/70 backdrop-blur-sm placeholder-gray-500 text-gray-800"
                  placeholder="Digite a nova senha"
                />
                
                {/* Indicadores de requisitos */}
                {novaSenha && (
                  <div className="mt-3 space-y-1 text-sm">
                    <div className={`flex items-center ${novaSenha.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                      <span className="mr-2">{novaSenha.length >= 8 ? '✓' : '○'}</span>
                      Mínimo 8 caracteres
                    </div>
                    <div className={`flex items-center ${/[a-zA-Z]/.test(novaSenha) ? 'text-green-600' : 'text-gray-500'}`}>
                      <span className="mr-2">{/[a-zA-Z]/.test(novaSenha) ? '✓' : '○'}</span>
                      Pelo menos uma letra
                    </div>
                    <div className={`flex items-center ${/[0-9]/.test(novaSenha) ? 'text-green-600' : 'text-gray-500'}`}>
                      <span className="mr-2">{/[0-9]/.test(novaSenha) ? '✓' : '○'}</span>
                      Pelo menos um número
                    </div>
                  </div>
                )}
              </div>

              <div>
                <p className="text-gray-700 font-semibold text-[2vh] mb-2">
                  Confirmar Senha
                </p>
                <input
                  type="password"
                  required
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  className="w-full px-5 py-3.5 border border-green-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 bg-white/70 backdrop-blur-sm placeholder-gray-500 text-gray-800"
                  placeholder="Confirme a nova senha"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white py-3 px-4 rounded-xl font-semibold shadow-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 disabled:opacity-70"
              >
                {isLoading ? "Redefinindo..." : "Redefinir Senha"}
              </button>
            </form>
          )}
        </div>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            © {new Date().getFullYear()} CMD Automação
          </p>
        </div>
      </div>
    </div>
  );
}